import { derivePalette, deriveTokens, type ThemeRecipe } from './recipe';
import type { CustomTheme, Theme } from './types';

// Sentinel theme key meaning "the legacy inline profile.theme.custom
// recipe" — pre-v4 trees stored a one-shot recipe there. The v3 → v4
// migration promotes those into `SyncedTree.customThemes` and rewrites
// `theme.default` to the library entry's id, but the resolver keeps
// this sentinel path for one release to survive any stragglers.
//
// Lives in this module (not `registry.ts`) so the registry can call
// into `buildCustomTheme` without importing customTheme + creating a
// circular import.
export const CUSTOM_THEME_KEY = 'custom';

// Synthesize a Theme from a ThemeRecipe (legacy) or a full CustomTheme
// library entry (v4+). The legacy signature is retained so
// `resolveTheme` can serve pre-migration boot code that never sees the
// family library; the library overload is what every store-aware
// consumer (useActiveTheme + resolveThemeWithLibrary) uses.
export function buildCustomTheme(recipe: ThemeRecipe): Theme;
export function buildCustomTheme(custom: CustomTheme): Theme;
export function buildCustomTheme(input: ThemeRecipe | CustomTheme): Theme {
  return isCustomThemeInput(input) ? buildFromCustom(input) : buildFromRecipe(input);
}

function buildFromRecipe(recipe: ThemeRecipe): Theme {
  return {
    key: CUSTOM_THEME_KEY,
    name: 'Oma',
    tokens: deriveTokens(recipe),
    routineCardPalette: derivePalette(recipe),
  };
}

function buildFromCustom(custom: CustomTheme): Theme {
  const usesManualPalette =
    custom.paletteMode === 'manual' && Array.isArray(custom.paletteOverride);
  const routineCardPalette = usesManualPalette
    ? custom.paletteOverride!.slice()
    : derivePalette(custom.recipe);

  const theme: Theme = {
    key: custom.id,
    name: custom.name,
    tokens: deriveTokens(custom.recipe),
    routineCardPalette,
    // Marker consumed by `<DecorationRenderer>` (see #46 §D5) and by
    // effect consumers in #47. Presets never set this.
    source: 'custom',
    customDecoration: custom.decoration,
    customTapSpec: custom.tapEffect,
    customCelebrationSpec: custom.celebration,
    customSoundSpec: custom.sound,
  };
  return theme;
}

// Discriminates the overload argument. `CustomTheme` always carries an
// `id` string; `ThemeRecipe` does not. Narrow enough that a caller can't
// accidentally pass an object shaped like both.
function isCustomThemeInput(value: ThemeRecipe | CustomTheme): value is CustomTheme {
  return typeof (value as Partial<CustomTheme>).id === 'string';
}
